/**
 * Whether a socket that went away is worth writing down, and what to say.
 *
 * The first thing the error table filled up with was `close 1006 · connection
 * lost`, over and over, from devices whose owners had noticed nothing: a phone
 * changing cell, a screen that had been off long enough for the server's
 * heartbeat to reap the socket, a tunnel. The app is built to survive exactly
 * that — the chat list says «нет соединения», the reconnect backoff brings the
 * socket back, the thread resyncs — so a row per drop describes the recovery
 * mechanism working, not a fault, and it does it loudly enough to hide the
 * rows that are faults.
 *
 * So the fact worth recording is not "the connection dropped" but "the
 * connection did not come back while somebody was looking at it". Below the
 * grace window this returns null and the outage goes unrecorded; past it, one
 * row says how long the connection had been up and how long it has been failing
 * to return since.
 *
 * The second half of that sentence is what the grace window is measured
 * against, and it is not wall-clock time. A backgrounded phone still runs the
 * reconnect timer — throttled to about once a minute on Android — and those
 * attempts fail against a sleeping radio, so a screen switched off for ten
 * minutes filed a «still down after 517s» that ended the instant it was
 * switched back on. Nobody was ever without a chat: while the app is off screen
 * a message arrives as a push, which does not need this socket at all. Only
 * time the app spent on screen and disconnected counts, which is exactly the
 * time somebody could have been looking at «нет соединения» and wondering.
 */

/**
 * How long a socket has to stay down, with the app on screen, before the outage
 * is a fact rather than a blip.
 *
 * Half a minute is chosen off the reconnect backoff, which tops out at 15s and
 * so makes attempts at roughly 0, 1, 3, 7, 15 and 30 seconds: the first attempt
 * that fails past this window is the sixth, which is late enough that nothing
 * transient survives it and early enough that the row still lands while the
 * device is awake to send it.
 */
export const OUTAGE_GRACE_MS = 30_000;

export interface OutageFacts {
  /** The browser's close code. */
  code: number;
  /** What the server said, when it said anything. */
  reason: string;
  wasClean: boolean;
  /** How long the connection had been up before it went, or null if it never was. */
  uptimeMs: number | null;
  /** How long it has been failing to come back, across every retry so far. */
  downForMs: number;
  /** How much of that the app spent on screen. Never more than `downForMs`. */
  visibleDownForMs: number;
}

export interface OutageNote {
  detail: string;
  message: string;
}

/**
 * 1006 is set by the browser when the connection died with no close frame at
 * all, and 1005 when a frame arrived carrying no code. Neither is the server
 * speaking, which is what makes them indistinguishable from a network that
 * simply went away — and why they are the two that have to wait.
 *
 * 1001 waits for a different reason. It means "this side is going away", and
 * the side saying it is almost always the browser: closing the tab, or leaving
 * the page, closes the socket with 1001 while the app still wants it open, so
 * the last socket of every session ended up in the table as a fault. Nothing
 * retries a socket whose page is gone, so that close now never reaches the
 * grace window and is never written down; a 1001 that was the server restarting
 * is held to the same rule as any other drop and reported only if the socket
 * fails to come back.
 */
function endedItself(code: number): boolean {
  return code === 1006 || code === 1005 || code === 1001;
}

/**
 * The note for this outage, or null while it is still young enough to be
 * nothing.
 *
 * A code that means the server was reached and refused is reported at once and
 * never waits: that is a fact a successful reconnect a second later does not
 * un-say. Only the drops where one side or the other simply went away are held
 * back.
 */
export function outageNote(facts: OutageFacts, grace = OUTAGE_GRACE_MS): OutageNote | null {
  if (endedItself(facts.code) && facts.visibleDownForMs < grace) return null;

  return {
    detail: `close ${facts.code}`,
    message: [
      facts.reason || (facts.wasClean ? 'closed cleanly' : 'connection lost'),
      facts.uptimeMs === null ? 'never connected' : `up for ${seconds(facts.uptimeMs)}`,
      // Left off when there is nothing to say — a server-sent code is written
      // down on the spot, and «still down after 0s» would be noise on a line
      // whose whole job is to be read quickly.
      ...(facts.downForMs >= 1_000 ? [downLeg(facts)] : []),
    ].join(' · '),
  };
}

/**
 * How long it has been down, and — when they differ — how much of that anyone
 * could see. The row is filed on the second figure, so a line that says 600s
 * without it would look like ten minutes of somebody staring at «нет
 * соединения» when it was ten minutes in a pocket and thirty seconds of staring.
 */
function downLeg(facts: OutageFacts): string {
  const down = `still down after ${seconds(facts.downForMs)}`;
  return facts.downForMs - facts.visibleDownForMs >= 1_000
    ? `${down}, ${seconds(facts.visibleDownForMs)} of it on screen`
    : down;
}

function seconds(ms: number): string {
  return `${Math.round(ms / 1000)}s`;
}
